import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
    IApiResponse,
    IPaginatedApiResponse,
} from "../../../utils/types/api-response";
import { handleRemoveEmptyParamFromQueryParams } from "../../../utils/helpers";
import {
    getSingleClaim,
    fetchClaims,
    fetchClaimsForRemit,
    fetchClaimSubmissionHistory,
    fetchClaimsAttachments,
    getClaimsListsStats,
    getParentClaim,
    fetchRejectionReasons,
} from "../services/claims.services";
import {
    SingleClaimResponse,
    IClaim,
    IClaimForRemit,
    IClaimsFilters,
    IGetClaimFilter,
    IClaimSubmissionHistoryFilters,
    IClaimSubmissionHistory,
    IGetClaimsAttachment,
    IGetClaimsAttachmentResponse,
    IGetClaimsListsStats,
    IParentClaimChildClaim,
    IParentClaimDetails,
} from "../types/claims.types";

export function useGetClaim(filters: IGetClaimFilter, isEnabled: boolean) {
    return useQuery<SingleClaimResponse, AxiosError<SingleClaimResponse>>({
        queryKey: ["get-claim", handleRemoveEmptyParamFromQueryParams(filters)],
        queryFn: () => getSingleClaim(filters),
        enabled: isEnabled,
    });
}

export function useFetchClaimsAttachments(
    params: IGetClaimsAttachment,
    enabled?: boolean
) {
    return useQuery<
        IApiResponse<IGetClaimsAttachmentResponse[]>,
        AxiosError<IApiResponse<IGetClaimsAttachmentResponse[]>>
    >({
        queryKey: ["get-claims-attachment", params.claim_id],
        queryFn: () => fetchClaimsAttachments(params),
        enabled,
    });
}

export function useFetchClaims(filters: IClaimsFilters, enabled?: boolean) {
    return useQuery<
        IPaginatedApiResponse<IClaim[]>,
        AxiosError<IApiResponse<IClaim[]>>
    >({
        queryKey: ["claims", handleRemoveEmptyParamFromQueryParams(filters)],
        queryFn: () => fetchClaims(filters),
        enabled,
    });
}

export function useFetchClaimsForRemit(
    filters: IClaimsFilters,
    enabled?: boolean
) {
    return useQuery<
        IPaginatedApiResponse<IClaimForRemit[]>,
        AxiosError<IApiResponse<IClaimForRemit[]>>
    >({
        queryKey: [
            "claims-for-remit",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchClaimsForRemit(filters),
        enabled,
    });
}

export function useGetClaimsListsStats(
    filters: IClaimsFilters,
    enabled?: boolean
) {
    return useQuery<
        IApiResponse<IGetClaimsListsStats>,
        AxiosError<IApiResponse<IGetClaimsListsStats>>
    >({
        queryKey: [
            "claims-stats",
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => getClaimsListsStats(filters),
        enabled,
    });
}

export function useFetchClaimSubmissionHistory(
    claimId: string,
    filters: IClaimSubmissionHistoryFilters
) {
    return useQuery<
        IPaginatedApiResponse<IClaimSubmissionHistory[]>,
        AxiosError<IApiResponse<IClaimSubmissionHistory[]>>
    >({
        queryKey: [
            "claim-submission-history",
            claimId,
            handleRemoveEmptyParamFromQueryParams(filters),
        ],
        queryFn: () => fetchClaimSubmissionHistory(claimId, filters),
    });
}

export function useFetchRejectionReasons(enabled?: boolean) {
    return useQuery<IApiResponse<string[]>, AxiosError<IApiResponse<string[]>>>(
        {
            queryKey: ["rejection-reasons"],
            queryFn: () => fetchRejectionReasons(),
            enabled,
        }
    );
}

export function useGetParentClaim(parentClaimId: string, isEnabled: boolean) {
    return useQuery<
        IApiResponse<{
            parent_claim: IParentClaimDetails;
            child_claims: IParentClaimChildClaim[];
        }>,
        AxiosError<
            IApiResponse<{
                parent_claim: IParentClaimDetails;
                child_claims: IParentClaimChildClaim[];
            }>
        >
    >({
        queryKey: ["parent-claim", parentClaimId],
        queryFn: () => getParentClaim(parentClaimId),
        enabled: isEnabled,
    });
}
